// src/pages/bloomterminal/BloomTerminalFAQ.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, SlidersVertical, MessageSquare, PlayCircle, ArrowRight } from 'lucide-react';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import { useTutorial } from '../context/TutorialContext';

const CONTACT_FORM_URL = 'https://bloomiq.io/contact/';
const SUPPORT_EMAIL = 'support@bloomiq.io';

const BloomTerminalFAQ = () => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const { startTutorial } = useTutorial();

  // The tour is no longer one of these — it has its own banner above the list.
  const faqItems = [
    {
      question: t('faq.cancelPlanQuestion'),
      answer: t('faq.cancelPlanAnswer'),
      icon: Ban,
    },
    {
      question: t('faq.upgradePlanQuestion'),
      answer: t('faq.upgradePlanAnswer'),
      icon: SlidersVertical,
    },
    {
      question: t('faq.contactQuestion'),
      answer: (
        <>
          {t('faq.contactAnswerPart1')}{' '}
          <a
            href={CONTACT_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-bloom-green hover:underline"
          >
            bloomiq.io/contact
          </a>{' '}
          {t('faq.contactAnswerPart2')}{' '}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="font-medium text-bloom-green hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </>
      ),
      icon: MessageSquare,
    },
  ];

  return (
    <div className="h-full w-full overflow-y-auto bg-dark-app">
      {/* Same page gutter as Usage/Billing/Account — the content runs the full
          width of the column rather than sitting in a narrow centred measure. */}
      <div className="w-full px-[16px] pt-[24px] pb-[32px] md:px-[32px]">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="text-center">
          <h1
            className="font-['Inter'] font-bold text-[#282A29] dark:text-[#f0f5f2]"
            style={{ fontSize: fontSize + 10 }}
          >
            {t('faq.title')}
          </h1>
          <p
            className="mt-2 font-['Inter'] font-normal text-[#5D625F] dark:text-[#b9bfbb]"
            style={{ fontSize: fontSize - 2 }}
          >
            {t('faq.subtitle')}
          </p>
        </div>

        {/* ── Tour banner ─────────────────────────────────────────────── */}
        {/* Stacks under sm: the button would otherwise squeeze the copy to a
            couple of words per line on a phone. */}
        <div className="mt-8 flex flex-col gap-4 rounded-[12px] border border-[#DCE7DF] bg-gradient-to-r from-[#E7F6EE] to-[#EDF3FC] p-4 dark:border-[#3a3c3b] dark:from-bloom-green/15 dark:to-[#1d1d1d] sm:flex-row sm:items-center">
          <div className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-[10px] bg-bloom-green">
            <PlayCircle size={22} strokeWidth={1.75} className="text-white" />
          </div>

          <div className="min-w-0 flex-1">
            <h2
              className="font-['Inter'] font-bold text-bloom-green"
              style={{ fontSize: fontSize - 1 }}
            >
              {t('faq.tourTitle')}
            </h2>
            <p
              className="mt-1 font-['Inter'] font-normal text-[#5D625F] dark:text-[#b9bfbb]"
              style={{ fontSize: fontSize - 2 }}
            >
              {t('faq.tourDescription')}
            </p>
          </div>

          <button
            type="button"
            onClick={startTutorial}
            className="flex shrink-0 items-center justify-center gap-2 rounded-[8px] bg-bloom-green px-4 py-[10px] font-['Inter'] font-semibold text-white transition-colors hover:bg-bloom-green-hover"
            style={{ fontSize: fontSize - 2 }}
          >
            {t('faq.startTour')}
            <ArrowRight size={16} strokeWidth={2} />
          </button>
        </div>

        {/* ── Question cards ──────────────────────────────────────────── */}
        <div className="mt-[40px] flex flex-col gap-[10px]">
          {faqItems.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={index}
                className="rounded-[12px] border border-[#E6E9E4] bg-white p-4 dark:border-[#3a3c3b] dark:bg-[#1d1d1d]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[8px] bg-[#E3F5EB] dark:bg-bloom-green/15">
                    <Icon size={16} strokeWidth={2} className="text-bloom-green" />
                  </div>
                  <h3
                    className="font-['Inter'] font-bold text-[#282A29] dark:text-[#f0f5f2]"
                    style={{ fontSize: fontSize - 1 }}
                  >
                    {item.question}
                  </h3>
                </div>

                {/* Indented past the icon tile (32px) + its gap (12px) so the
                    answer hangs off the question, not the icon. */}
                <p
                  className="mt-2 pl-[44px] font-['Inter'] font-normal text-[#5D625F] dark:text-[#b9bfbb]"
                  style={{ fontSize: fontSize - 2 }}
                >
                  {item.answer}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BloomTerminalFAQ;
